import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { userClient } from '../../lib/userClient'

interface ExploreTrip {
  id: string
  title: string
  type: string
  privacy: string
  status: string
  locations?: Array<{ title: string }>
  participants?: Array<{ id: string; name: string; avatar?: string }>
  likes?: number
  comments?: number
  favoritesCount?: number
  img?: string
}

export default function UserExplorePage() {
  const [trips, setTrips] = useState<ExploreTrip[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchExplore = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await userClient.getExplore()
      const payload = (response.data?.data || []) as ExploreTrip[]
      setTrips(Array.isArray(payload) ? payload : [])
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load explore feed'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchExplore()
  }, [fetchExplore])

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Explore creators you follow</h2>
          <p className="text-sm text-gray-600">
            Powered by <code>GET /trips/following</code>, this feed mirrors what users see in the native app.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchExplore}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
        >
          Refresh
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : trips.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
            No trips from followed creators yet. Start following users to see their journeys here.
          </div>
        ) : (
          trips.map((trip) => (
            <article key={trip.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-sm transition">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="uppercase font-semibold tracking-wide">{trip.type}</span>
                <span className="capitalize">{trip.status}</span>
              </div>
              <h3 className="mt-2 text-base font-semibold text-gray-900">{trip.title}</h3>
              {trip.locations && trip.locations.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {trip.locations.map((location) => location.title).join(', ')}
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
                <span>Privacy: {trip.privacy}</span>
                {typeof trip.likes === 'number' && <span>Likes: {trip.likes}</span>}
                {typeof trip.comments === 'number' && <span>Comments: {trip.comments}</span>}
                {typeof trip.favoritesCount === 'number' && <span>Favorites: {trip.favoritesCount}</span>}
              </div>
              {trip.participants && trip.participants.length > 0 && (
                <div className="mt-3 text-xs text-gray-500">
                  Participants: {trip.participants.map((participant) => participant.name).filter(Boolean).join(', ')}
                </div>
              )}
            </article>
          ))
        )}
      </div>
    </section>
  )
}
